import {
  Add,
  Delete,
  FilterAlt,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Refresh,
  Save,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import { useEffect, useState } from "react";
import { Company } from "../../types";
import { hostname, parseError } from "../../utils";

interface ContentPillar {
  id: string;
  title: string;
  description: string;
  topics: string[];
  stage: "tofu" | "mofu" | "bofu";
}

interface ContentStrategy {
  id?: string;
  companyId: string;
  salesNarrative: string;
  targetAudience: string;
  pillars: ContentPillar[];
  created_date?: string;
  updated_date?: string;
}

const EMPTY_STRATEGY: ContentStrategy = {
  companyId: "",
  salesNarrative: "",
  targetAudience: "",
  pillars: [
    {
      id: "pillar1",
      title: "",
      description: "",
      topics: [],
      stage: "tofu",
    },
    {
      id: "pillar2",
      title: "",
      description: "",
      topics: [],
      stage: "mofu",
    },
    {
      id: "pillar3",
      title: "",
      description: "",
      topics: [],
      stage: "bofu",
    },
  ],
};

export default ({
  currentUser,
  getToken,
  openDialog,
  eventListener,
  companyId,
  company,
  currentPlan,
  strategyId = null,
}: {
  getToken: Function;
  openDialog: Function;
  eventListener: Function;
  currentUser: any | User;
  companyId: string;
  company: Company;
  currentPlan: string;
  strategyId?: string | null;
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [strategy, setStrategy] = useState<ContentStrategy>({
    ...EMPTY_STRATEGY,
    companyId,
  });
  const [expandedPillar, setExpandedPillar] = useState<string | null>(null);

  const handlePillarExpand = (pillarId: string) => {
    setExpandedPillar(expandedPillar === pillarId ? null : pillarId);
  };

  // Fetch the current content strategy
  const fetchStrategy = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const url = strategyId
        ? `${hostname}/content/strategy/${strategyId}`
        : `${hostname}/content/strategy`;

      const response = await axios.get(url, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data) {
        setStrategy(response.data);
      } else {
        setStrategy({
          ...EMPTY_STRATEGY,
          companyId,
        });
      }
    } catch (error) {
      console.error("Error fetching content strategy:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
      // Set default empty strategy if we can't fetch one
      setStrategy({
        ...EMPTY_STRATEGY,
        companyId,
      });
    } finally {
      setLoading(false);
    }
  };

  // Save the current content strategy
  const saveStrategy = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/content/strategy`,
        { strategy, companyId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      eventListener({
        type: "SET_INFO",
        info: "Content strategy saved successfully",
      });

      // Refresh to get the updated strategy
      fetchStrategy();
    } catch (error) {
      console.error("Error saving content strategy:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle input changes for salesNarrative and targetAudience
  const handleInputChange = (field: string, value: string) => {
    setStrategy({
      ...strategy,
      [field]: value,
    });
  };

  // Add a new pillar to a specific funnel stage
  const addPillar = (stage: "tofu" | "mofu" | "bofu") => {
    const newPillar: ContentPillar = {
      id: `pillar_${Date.now()}`,
      title: "",
      description: "",
      topics: [],
      stage,
    };

    setStrategy({
      ...strategy,
      pillars: [...strategy.pillars, newPillar],
    });
  };

  // Delete a pillar by its ID
  const deletePillar = (pillarId: string) => {
    setStrategy({
      ...strategy,
      pillars: strategy.pillars.filter((pillar) => pillar.id !== pillarId),
    });
  };

  // Update a pillar by its ID
  const updatePillarById = (pillarId: string, field: string, value: any) => {
    setStrategy({
      ...strategy,
      pillars: strategy.pillars.map((pillar) =>
        pillar.id === pillarId ? { ...pillar, [field]: value } : pillar
      ),
    });
  };

  // Handle topics change by pillar ID
  const handleTopicsChangeById = (pillarId: string, topicsString: string) => {
    const topicsArray = topicsString
      .split(",")
      .map((topic) => topic.trim())
      .filter((topic) => topic.length > 0);

    updatePillarById(pillarId, "topics", topicsArray);
  };

  // Load strategy on component mount or when strategyId changes
  useEffect(() => {
    if (companyId) {
      fetchStrategy();
    }
  }, [companyId, strategyId]);

  const getFunnelStageName = (stage: string) => {
    switch (stage.toLowerCase()) {
      case "tofu":
        return "Top of Funnel (Awareness)";
      case "mofu":
        return "Middle of Funnel (Consideration)";
      case "bofu":
        return "Bottom of Funnel (Decision)";
      default:
        return stage;
    }
  };

  // Helper to display funnels with editable pillars
  const renderFunnelSection = (
    pillars: ContentPillar[],
    stage: "tofu" | "mofu" | "bofu"
  ) => {
    const stagePillars = pillars.filter((pillar) => pillar.stage === stage);

    return (
      <Box sx={{ mb: 2 }}>
        <Card
          variant="outlined"
          sx={{
            borderLeft: `5px solid ${stage === "tofu" ? "#2e7d32" : // success.dark
                        stage === "mofu" ? "#0288d1" : // info.dark
                        stage === "bofu" ? "#7b1fa2" : // secondary.dark
                        "#757575"}` // default
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">
                {getFunnelStageName(stage)}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={() => addPillar(stage)}
              >
                Add Pillar
              </Button>
            </Box>

            {stagePillars.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No content pillars in this stage yet. Click "Add Pillar" to create one.
              </Typography>
            ) : (
              stagePillars.map((pillar) => (
                <Accordion
                  key={pillar.id}
                  expanded={expandedPillar === pillar.id}
                  onChange={() => handlePillarExpand(pillar.id)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary
                    expandIcon={
                      expandedPillar === pillar.id ? (
                        <KeyboardArrowUp />
                      ) : (
                        <KeyboardArrowDown />
                      )
                    }
                  >
                    <Box sx={{ display: "flex", alignItems: "center", width: "100%", pr: 2 }}>
                      <Typography sx={{ flexGrow: 1 }}>{pillar.title || "Untitled Pillar"}</Typography>
                      <Tooltip title="Delete Pillar">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            deletePillar(pillar.id);
                          }}
                          sx={{ ml: 1 }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="Pillar Title"
                          fullWidth
                          size="small"
                          value={pillar.title}
                          onChange={(e) =>
                            updatePillarById(pillar.id, "title", e.target.value)
                          }
                          placeholder="Enter pillar title..."
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Description"
                          multiline
                          rows={2}
                          fullWidth
                          size="small"
                          value={pillar.description}
                          onChange={(e) =>
                            updatePillarById(pillar.id, "description", e.target.value)
                          }
                          placeholder="Describe this content pillar..."
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Topics (comma-separated)"
                          fullWidth
                          size="small"
                          value={pillar.topics.join(", ")}
                          onChange={(e) =>
                            handleTopicsChangeById(pillar.id, e.target.value)
                          }
                          placeholder="Topic 1, Topic 2, Topic 3"
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Move to Stage</InputLabel>
                          <Select
                            value={pillar.stage}
                            label="Move to Stage"
                            onChange={(e) =>
                              updatePillarById(pillar.id, "stage", e.target.value)
                            }
                          >
                            <MenuItem value="tofu">Top of Funnel (Awareness)</MenuItem>
                            <MenuItem value="mofu">Middle of Funnel (Consideration)</MenuItem>
                            <MenuItem value="bofu">Bottom of Funnel (Decision)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5">Content Strategy</Typography>
          <Box>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Refresh />}
              onClick={fetchStrategy}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={saveStrategy}
              disabled={saving || loading}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>About Content Strategy</AlertTitle>A content strategy
          helps you create targeted content with maximum impact. It analyzes
          your company information, ideal customer profile, and other inputs to
          define sales narratives and content pillars aligned with different
          stages of the sales funnel.
        </Alert>

        {loading ? (
          <Box sx={{ width: "100%", mt: 3, mb: 3 }}>
            <LinearProgress />
            <Typography sx={{ mt: 2, textAlign: "center" }}>
              Loading content strategy...
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Core Strategy
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Sales Narrative"
                    multiline
                    rows={4}
                    fullWidth
                    value={strategy.salesNarrative}
                    onChange={(e) =>
                      handleInputChange("salesNarrative", e.target.value)
                    }
                    placeholder="Define your core sales narrative..."
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Target Audience"
                    multiline
                    rows={3}
                    fullWidth
                    value={strategy.targetAudience}
                    onChange={(e) =>
                      handleInputChange("targetAudience", e.target.value)
                    }
                    placeholder="Describe your target audience..."
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ mb: 4 }} />

            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <FilterAlt color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Content Funnel</Typography>
              </Box>

              {/* TOFU section */}
              {renderFunnelSection(strategy.pillars, "tofu")}

              {/* MOFU section */}
              {renderFunnelSection(strategy.pillars, "mofu")}

              {/* BOFU section */}
              {renderFunnelSection(strategy.pillars, "bofu")}
            </Box>

          </>
        )}
      </Paper>
    </Box>
  );
};
